import React, { useState, useEffect, useRef } from "react";
import {
    Button,
    Alert,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Row,
    Col,
    Spinner,
    Collapse,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API } from "../../../api/urls";
import { fetchActivePosByCompany, deleteGroup as deleteGroupApi, fetchGroups as fetchGroupsApi, saveGroup as saveGroupApi } from "../../../api/requests/company-requests";

const CARD_TYPES = [
    "WORLD",
    "AXESS",
    "MAXIMUM",
    "CARDFINANS",
    "PARAF",
    "ADVANTAGE",
    "COMBO",
    "BONUS",
    "SAGLAMKART",
    "STANDART",
];

export const CompanyGroupSettings = ({ companyId }) => {
    const [loading, setLoading] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [groups, setGroups] = useState([]);
    const [groupData, setGroupData] = useState({
        id: null,
        groupName: "",
        description: "",
        rates: {},
    });
    const [openCard, setOpenCard] = useState(CARD_TYPES[0]);
    const MAX_INSTALLMENTS = 12;

    const toggleCardAccordion = (card) => {
        setOpenCard((prev) => (prev === card ? null : card));
    };

    const showToast = (type, msg) => {
        const map = { danger: "error", error: "error" };
        const fn = toast[map[type] || type] || toast.info;
        fn(msg, { autoClose: 3000, containerId: "global-toaster" });
    };

    // Backend'den gelen eski installmentRates listelerini frontend formatına çevirir
    const convertBackendToFrontend = (installmentRates = []) => {
        const rates = {};

        installmentRates.forEach((r) => {
            const brand = (r.brand ?? r.cardBrand ?? "").toUpperCase();
            if (!rates[brand]) rates[brand] = {};

            rates[brand][r.installmentCount] = {
                id: r.id ?? null,
                value: Number(r.commissionPercent ?? r.commissionRate ?? 0).toFixed(2),
                enabled: r.enabled != null ? r.enabled : true,
            };
        });

        // Eksikleri tamamla, default enabled=true
        CARD_TYPES.forEach((card) => {
            if (!rates[card]) rates[card] = {};
            for (let i = 1; i <= MAX_INSTALLMENTS; i++) {
                if (!rates[card][i]) {
                    rates[card][i] = { id: null, value: "0.00", enabled: true };
                }
            }
        });

        return rates;
    };

    // Frontend formatını backend'e göndermek için dönüştürür
    const convertFrontendToBackend = (ratesObj = {}) => {
        const list = [];

        Object.entries(ratesObj).forEach(([cardBrand, counts]) => {
            Object.entries(counts).forEach(([instCnt, { id, value, enabled }]) => {
                if (value != null && value !== "") {
                    const commission = parseFloat(value);
                    if (!Number.isNaN(commission)) {
                        list.push({
                            id,
                            cardBrand,
                            installmentCount: parseInt(instCnt, 10),
                            commissionPercent: commission,
                            enabled: enabled,
                        });
                    }
                }
            });
        });

        return list;
    };

    // Aktif POS endpoint'inden gelen banks[] yapısını frontend rates formatına çevirir
    const convertActivePosToFrontend = (banks = []) => {
        const rates = {};

        // Önce gelen bankalara göre doldur
        banks.forEach((bank) => {
            const brand = (bank.cardBrand ?? "").toUpperCase();
            if (!brand) return;
            if (!rates[brand]) rates[brand] = {};

            (bank.installments || []).forEach((inst) => {
                rates[brand][inst.installmentCount] = {
                    id: null,
                    value:
                        inst.commissionPercent !== null &&
                        inst.commissionPercent !== undefined
                            ? Number(inst.commissionPercent).toFixed(2)
                            : "0.00",
                    enabled:
                        inst.enabled === undefined || inst.enabled === null
                            ? true
                            : inst.enabled,
                };
            });
        });

        // Eksikleri tamamla (CARD_TYPES x 1..MAX_INSTALLMENTS)
        CARD_TYPES.forEach((card) => {
            if (!rates[card]) rates[card] = {};
            for (let i = 1; i <= MAX_INSTALLMENTS; i++) {
                if (!rates[card][i]) {
                    rates[card][i] = {
                        id: null,
                        value: "0.00",
                        enabled: true,
                    };
                }
            }
        });

        return rates;
    };

    // Aktif POS oranlarını çeken fonksiyon
    async function handleFetchActivePosRates() {
        if (!companyId) return;

        try {
            setLoading(true);
            const json = await fetchActivePosByCompany(companyId);

            if (json.data && Array.isArray(json.data.banks)) {
                const newRates = convertActivePosToFrontend(json.data.banks);
                setGroupData((prev) => ({
                    ...prev,
                    rates: newRates,
                }));
                showToast("success", "Aktif POS oranları yüklendi");
            } else {
                showToast("danger", json.message || "Aktif POS oranları yüklenemedi");
            }
        } catch (err) {
            console.error("Active POS rates error:", err);
            showToast("danger", "Aktif POS oranları alınamadı");
        } finally {
            setLoading(false);
        }
    }

    const handleDeleteClick = (id) => {
        setDeletingId(id);
        setConfirmModalOpen(true);
    };

    const confirmDelete = async () => {
        try {
            setLoading(true);
            const res = await deleteGroupApi(deletingId);
            if (!res.ok) throw new Error();
            showToast("success", "Cari/Bayi grup silindi");
            setConfirmModalOpen(false);
            fetchGroups();
        } catch (err) {
            console.error("Silme hatası:", err);
            showToast("danger", "Cari/Bayi grup silinemedi");
        } finally {
            setLoading(false);
        }
    };

    const fetchGroups = async () => {
        if (!companyId) return;

        try {
            setLoading(true);
            const groupsData = await fetchGroupsApi(companyId);
            setGroups(groupsData.data || []);
        } catch (error) {
            console.error("Groups fetch error:", error);
            setGroups([]);
            showToast("danger", "Taksit grupları yüklenemedi");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (companyId) {
            fetchGroups();
        }
    }, [companyId]);

    const toggleModal = () => setModalOpen((open) => !open);

    const openGroupModal = (group = null) => {
        if (group) {
            const newGroupData = {
                id: group.id,
                groupName: group.groupName || "",
                description: group.description || "",
                rates: convertBackendToFrontend(group.installmentRates || []),
            };
            setGroupData(newGroupData);
        } else {
            // Yeni grup: rates boş başlasın ki modal açıldığında aktif POS'tan otomatik çekebilelim
            const newGroupData = {
                id: null,
                groupName: "",
                description: "",
                rates: {}, // <-- önemli
            };
            setGroupData(newGroupData);
        }
        toggleModal();
    };

    const handleGroupChange = (e) => {
        const { name, value } = e.target;
        setGroupData((prev) => ({ ...prev, [name]: value }));
    };

    const handleRateChange = (card, inst, newVal) => {
        setGroupData((prev) => ({
            ...prev,
            rates: {
                ...prev.rates,
                [card]: {
                    ...(prev.rates?.[card] || {}),
                    [inst]: {
                        id: prev.rates?.[card]?.[inst]?.id || null,
                        value: newVal,
                        enabled: prev.rates?.[card]?.[inst]?.enabled !== false,
                    },
                },
            },
        }));
    };

    const handleToggleCard = (card, enabled) => {
        setGroupData((prev) => {
            const cardCounts = prev.rates?.[card] || {};
            const updatedCounts = {};
            Object.entries(cardCounts).forEach(([inst, val]) => {
                updatedCounts[inst] = {
                    ...val,
                    enabled: enabled,
                };
            });
            return {
                ...prev,
                rates: {
                    ...prev.rates,
                    [card]: updatedCounts,
                },
            };
        });
    };

    const handleSaveGroup = async () => {
        const groupName = groupData?.groupName?.trim();
        if (!groupName) {
            showToast("warning", "Grup adı gereklidir");
            return;
        }

        const isEdit = Boolean(groupData?.id);
        const payload = {
            groupName: groupName,
            description: groupData?.description?.trim() || "",
            companyId,
            installmentRates: convertFrontendToBackend(groupData?.rates || {}),
        };

        try {
            setLoading(true);
            await saveGroupApi({ id: groupData?.id, ...payload }, isEdit);
            showToast(
                "success",
                `Cari/Bayi grup ${isEdit ? "güncellendi" : "oluşturuldu"}`
            );
            toggleModal();
            fetchGroups();
        } catch (error) {
            console.error("Group save error:", error);
            showToast("danger", error.message || "Kaydetme hatası");
        } finally {
            setLoading(false);
        }
    };

    const handleEnabledChange = (card, inst, checked) => {
        setGroupData((prev) => ({
            ...prev,
            rates: {
                ...prev.rates,
                [card]: {
                    ...prev.rates[card],
                    [inst]: {
                        id: prev.rates[card]?.[inst]?.id || null,
                        value: prev.rates[card]?.[inst]?.value || "0.00",
                        enabled: checked,
                    },
                },
            },
        }));
    };

    // StrictMode'da aynı modal açılışında 2 kez fetch'i engellemek için
    const autoFetchedRef = useRef(false);

    useEffect(() => {
        // Modal kapandığında flag sıfırla
        if (!modalOpen) {
            autoFetchedRef.current = false;
            return;
        }

        // Sadece yeni grup + boş rates için auto fetch
        if (
            modalOpen &&
            companyId &&
            !groupData.id &&
            (!groupData.rates || Object.keys(groupData.rates).length === 0) &&
            !autoFetchedRef.current
        ) {
            autoFetchedRef.current = true;
            handleFetchActivePosRates();
        }
    }, [modalOpen, companyId, groupData.id, groupData.rates]);

    if (!companyId) {
        return (
            <Alert color="warning">
                Taksit grup ayarları için önce firma kaydetmelisiniz.
            </Alert>
        );
    }

    return (
        <div>
            <h5 className="mb-3">Taksit Grupları</h5>

            <Button
                color="primary"
                className="mb-3"
                onClick={() => openGroupModal()}
                style={{ borderRadius: "0" }}
                disabled={loading}
            >
                Taksit Grubu Oluştur
            </Button>

            {loading && groups.length === 0 ? (
                <div className="text-center p-4">
                    <Spinner size="sm" className="me-2" /> Gruplar yükleniyor...
                </div>
            ) : (
                <div className="list-group">
                    {groups.map((g) => (
                        <div
                            key={g.id}
                            className="list-group-item d-flex justify-content-between align-items-center"
                        >
                            <div>
                                <strong>{g.groupName}</strong>
                                {g.description && (
                                    <div className="text-muted small">{g.description}</div>
                                )}
                            </div>
                            <div>
                                <Button
                                    size="sm"
                                    color="secondary"
                                    onClick={() => openGroupModal(g)}
                                    disabled={loading}
                                    className="me-2"
                                >
                                    Düzenle
                                </Button>
                                <Button
                                    size="sm"
                                    color="danger"
                                    onClick={() => handleDeleteClick(g.id)}
                                    disabled={loading}
                                >
                                    Sil
                                </Button>
                            </div>
                        </div>
                    ))}
                    {groups.length === 0 && !loading && (
                        <div className="text-center p-3">
                            Henüz cari/bayi grubu yok.
                        </div>
                    )}
                </div>
            )}

            {/* Grup Modalı */}
            <Modal isOpen={modalOpen} toggle={toggleModal} size="xl" scrollable>
                <ModalHeader toggle={toggleModal}>
                    {groupData.id ? "Taksit Grubu Düzenle" : "Taksit Grubu Oluştur"}
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label>Grup Adı *</Label>
                            <Input
                                name="groupName"
                                value={groupData?.groupName || ""}
                                onChange={handleGroupChange}
                                style={{ borderRadius: "0" }}
                                required
                                disabled={loading}
                                placeholder="Grup adını giriniz"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Açıklama</Label>
                            <Input
                                type="textarea"
                                name="description"
                                value={groupData?.description || ""}
                                onChange={handleGroupChange}
                                style={{ borderRadius: "0" }}
                                rows={3}
                                disabled={loading}
                                placeholder="Grup açıklaması (opsiyonel)"
                            />
                        </FormGroup>
                        <hr />
                        <h5>Taksit Oranları</h5>
                        <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
                            <Button
                                color="info"
                                onClick={handleFetchActivePosRates}
                                className="btn-primary"
                                disabled={loading || !companyId}
                                style={{ marginRight: "1rem", borderRadius: "0" }}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Yükleniyor...
                                    </>
                                ) : (
                                    "Aktif POS oranlarını getir"
                                )}
                            </Button>
                        </div>

                        {CARD_TYPES.map((card) => {
                            const isOpen = openCard === card;

                            return (
                                <div key={card} className="mb-3 border rounded">
                                    {/* Akordeon başlığı */}
                                    <div
                                        className="d-flex align-items-center justify-content-between px-3 py-2"
                                        style={{
                                            cursor: "pointer",
                                            backgroundColor: "#f1f3f5",
                                        }}
                                        onClick={() => toggleCardAccordion(card)}
                                    >
                                        <strong className="text-primary mb-0">{card}</strong>
                                        <div className="d-flex align-items-center gap-2">
                                            <Button
                                                size="sm"
                                                color="success"
                                                style={{ borderRadius: "0" }}
                                                disabled={loading}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleToggleCard(card, true);
                                                }}
                                            >
                                                Tümünü Seç
                                            </Button>
                                            <Button
                                                size="sm"
                                                color="danger"
                                                style={{ borderRadius: "0" }}
                                                disabled={loading}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleToggleCard(card, false);
                                                }}
                                            >
                                                Tümünü Kaldır
                                            </Button>
                                            <span
                                                style={{
                                                    fontSize: "0.9rem",
                                                    color: "#6c757d",
                                                }}
                                            >
                                                {isOpen ? "▲" : "▼"}
                                            </span>
                                        </div>
                                    </div>

                                    <Collapse isOpen={isOpen}>
                                        <div className="px-3 pb-3 pt-2">
                                            <Row className="g-2">
                                                {[...Array(MAX_INSTALLMENTS)].map((_, i) => {
                                                    const inst = i + 1;
                                                    const cellData =
                                                        groupData.rates?.[card]?.[inst];
                                                    const isEnabled = cellData
                                                        ? cellData.enabled !== false
                                                        : true;
                                                    const cellValue = cellData
                                                        ? cellData.value || ""
                                                        : "";

                                                    return (
                                                        <Col
                                                            key={inst}
                                                            xs={6}
                                                            sm={4}
                                                            md={3}
                                                            lg={2}
                                                        >
                                                            <div
                                                                className="border p-3"
                                                                style={{
                                                                    borderRadius: "8px",
                                                                    backgroundColor: "#f8f9fa",
                                                                }}
                                                            >
                                                                {/* Checkbox + label */}
                                                                <div
                                                                    className="d-flex align-items-center mb-3"
                                                                    onClick={() =>
                                                                        handleEnabledChange(
                                                                            card,
                                                                            inst,
                                                                            !isEnabled
                                                                        )
                                                                    }
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        userSelect: "none",
                                                                    }}
                                                                >
                                                                    <input
                                                                        className="form-check-input me-2"
                                                                        type="checkbox"
                                                                        checked={isEnabled}
                                                                        readOnly
                                                                        style={{
                                                                            marginTop: 0,
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />
                                                                    <label
                                                                        className="form-check-label mb-0 fw-medium"
                                                                        style={{
                                                                            cursor: "pointer",
                                                                            fontSize: "0.875rem",
                                                                            color: isEnabled
                                                                                ? "#495057"
                                                                                : "#6c757d",
                                                                        }}
                                                                    >
                                                                        {inst} Taksit
                                                                    </label>
                                                                </div>

                                                                {/* Komisyon input */}
                                                                <div>
                                                                    <Label className="small mb-2 text-muted fw-medium">
                                                                        Komisyon (%)
                                                                    </Label>
                                                                    <Input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        max="100"
                                                                        value={cellValue}
                                                                        onChange={(e) =>
                                                                            handleRateChange(
                                                                                card,
                                                                                inst,
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            loading ||
                                                                            !isEnabled
                                                                        }
                                                                        style={{
                                                                            borderRadius: "6px",
                                                                            border: isEnabled
                                                                                ? "1px solid #ced4da"
                                                                                : "1px solid #e9ecef",
                                                                            backgroundColor: isEnabled
                                                                                ? "#fff"
                                                                                : "#f8f9fa",
                                                                        }}
                                                                        size="sm"
                                                                        placeholder="0.00"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        </div>
                                    </Collapse>
                                </div>
                            );
                        })}
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={toggleModal}
                        style={{ borderRadius: "0" }}
                        disabled={loading}
                    >
                        İptal
                    </Button>
                    <Button
                        color="success"
                        onClick={handleSaveGroup}
                        className="btn-primary"
                        style={{ borderRadius: "0" }}
                        disabled={!groupData.groupName?.trim() || loading}
                    >
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Kaydediliyor...
                            </>
                        ) : (
                            "Kaydet"
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Silme Onay Modalı */}
            <Modal
                isOpen={confirmModalOpen}
                toggle={() => setConfirmModalOpen(false)}
            >
                <ModalHeader toggle={() => setConfirmModalOpen(false)}>
                    Silme İşlemini Onayla
                </ModalHeader>
                <ModalBody>
                    Bu cari/bayi grubu silmek istediğine emin misin?
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={() => setConfirmModalOpen(false)}
                        disabled={loading}
                    >
                        Hayır
                    </Button>
                    <Button
                        color="danger"
                        onClick={confirmDelete}
                        disabled={loading}
                    >
                        Evet, Sil
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};